import { useCallback, useEffect, useRef } from 'react'
import { useUIStore } from '../store/uiStore.js'

export type SoundName =
  | 'button-press' | 'invalid-action' | 'turn-start' | 'ship-place'
  | 'strike-miss' | 'strike-hit'
  | 'torpedo-launch' | 'torpedo-travel'
  | 'sonar-ping' | 'sonar-result'
  | 'ship-sunk'
  | 'victory' | 'defeat'

type Category = 'ui' | 'combat' | 'ambient'

const ALL_SOUNDS: SoundName[] = [
  'button-press', 'invalid-action', 'turn-start', 'ship-place',
  'strike-miss', 'strike-hit',
  'torpedo-launch', 'torpedo-travel',
  'sonar-ping', 'sonar-result',
  'ship-sunk',
  'victory', 'defeat',
]

const CATEGORY_VOLUMES: Record<Category, number> = {
  combat: 1.0,
  ui: 0.7,
  ambient: 0.35,
}

// Singleton audio engine — shared across all hook instances
let audioCtx: AudioContext | null = null
let masterGain: GainNode | null = null
const busGains: Partial<Record<Category, GainNode>> = {}
const bufferCache = new Map<SoundName, AudioBuffer>()
const activeSources = new Set<AudioBufferSourceNode>()
let preloadPromise: Promise<void> | null = null

function initContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext()
    masterGain = audioCtx.createGain()
    masterGain.connect(audioCtx.destination)

    for (const cat of ['ui', 'combat', 'ambient'] as Category[]) {
      const gain = audioCtx.createGain()
      gain.gain.value = CATEGORY_VOLUMES[cat]
      gain.connect(masterGain)
      busGains[cat] = gain
    }
  }
  return audioCtx
}

async function ensureReady(): Promise<AudioContext> {
  const ctx = initContext()

  // Resume if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') {
    await ctx.resume()
  }

  // Ensure sounds are loaded
  if (!preloadPromise) {
    preloadPromise = doPreload(ctx)
  }
  await preloadPromise

  return ctx
}

async function doPreload(ctx: AudioContext) {
  // Determine best format by capability: opus ogg (smallest) → aac m4a
  // (Safari/iOS) → wav (universal fallback). Compressed files are ~10x
  // smaller, which matters on mobile connections.
  const formats: string[] = []
  try {
    const audio = new Audio()
    if (audio.canPlayType('audio/ogg; codecs=opus')) formats.push('ogg')
    if (audio.canPlayType('audio/mp4; codecs=mp4a.40.2')) formats.push('m4a')
  } catch {}
  formats.push('wav')

  await Promise.allSettled(ALL_SOUNDS.map(async (name) => {
    if (bufferCache.has(name)) return

    let loaded = false

    for (const fmt of formats) {
      try {
        const res = await fetch(`/sounds/${name}.${fmt}`)
        // Check both HTTP status and Content-Type to avoid SPA fallback pages
        const ct = res.headers.get('content-type') || ''
        if (!res.ok || !ct.startsWith('audio/')) continue

        const buf = await ctx.decodeAudioData(await res.arrayBuffer())
        bufferCache.set(name, buf)
        loaded = true
        break
      } catch {
        // Fetch/decode failed for this format — fall through to the next
      }
    }

    if (!loaded) {
      console.warn(`[audio] Failed to load: ${name}`)
    }
  }))

  console.log(`[audio] Preloaded ${bufferCache.size}/${ALL_SOUNDS.length} sounds`)
}

function playSoundSync(name: SoundName, category: Category = 'combat', loop = false): (() => void) | void {
  if (!audioCtx || !busGains[category]) return

  // Check mute state
  const { audioSettings } = useUIStore.getState()
  if (category === 'combat' && audioSettings.sfxMuted) return
  if (category === 'ui' && audioSettings.uiMuted) return
  if (category === 'ambient' && audioSettings.ambientMuted) return

  const buffer = bufferCache.get(name)
  if (!buffer) {
    console.warn(`[audio] No buffer for: ${name}`)
    return
  }

  const source = audioCtx.createBufferSource()
  source.buffer = buffer
  source.loop = loop
  source.connect(busGains[category]!)
  source.start()

  activeSources.add(source)
  source.onended = () => activeSources.delete(source)

  if (loop) {
    return () => {
      try { source.stop() } catch {}
      activeSources.delete(source)
    }
  }
}

function stopAllSounds() {
  for (const source of activeSources) {
    try { source.stop() } catch {}
  }
  activeSources.clear()
}

/**
 * Audio hook per Tech Lead ADR-003: raw Web Audio API, no library.
 * Lazy AudioContext init on first user interaction. Preloads all 13 SFX.
 */
export function useAudio() {
  const stopLoopRef = useRef<(() => void) | null>(null)

  // Kick off preload on first mount (may fail due to autoplay policy, that's OK)
  useEffect(() => {
    ensureReady().catch(() => {})
  }, [])

  // Tab visibility: mute on blur, restore on focus
  useEffect(() => {
    const handler = () => {
      if (!masterGain || !audioCtx) return
      if (document.hidden) {
        masterGain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.25)
      } else {
        masterGain.gain.linearRampToValueAtTime(1, audioCtx.currentTime + 0.25)
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  const play = useCallback((name: SoundName, category: Category = 'combat') => {
    // Await context + preload, then play
    ensureReady().then(() => {
      playSoundSync(name, category)
    })
  }, [])

  const loop = useCallback((name: SoundName, category: Category = 'combat') => {
    ensureReady().then(() => {
      const stop = playSoundSync(name, category, true)
      if (stop) {
        stopLoopRef.current = stop
      }
    })
    return () => {
      stopLoopRef.current?.()
      stopLoopRef.current = null
    }
  }, [])

  const stopAll = useCallback(() => {
    stopAllSounds()
    stopLoopRef.current = null
  }, [])

  return { play, loop, stopAll }
}
